/**
 * Fælles domænetyper og -konstanter for Skals GF.
 *
 * Denne fil deles af server og klient. Den må ikke importere serverspecifikke
 * moduler (database, node:crypto, miljøvariabler), fordi den ender i klientbundlen.
 */

/* ------------------------------------------------------------------ */
/* Afdelinger                                                          */
/* ------------------------------------------------------------------ */

/**
 * Appens afdelinger — foreningens syv.
 *
 * AQUASPINNING LÅ FØR UNDER SVØMNING. Det var ikke en beslutning, det var
 * en mangel: afdelingen fandtes ikke, og de fem hold skulle et sted hen.
 * Migration 0008 flytter dem, og kataloget siger nu `aquaspinning`.
 *
 * Badminton og minigolf er foreningens to øvrige udvalg. De kom til efter
 * de to første, og de har endnu ingen hold i appen — afdelingen findes,
 * fordi et udvalgsmedlem skal kunne tildeles den, og fordi bestyrelsen skal
 * kunne sende en besked til den. En tom afdeling er ærligere end en
 * afdeling, der ikke findes.
 *
 * RÆKKEFØLGEN BETYDER NOGET. Den styrer, hvordan afdelinger vises i lister
 * og i statistikken, og den skal være den samme overalt. Nye afdelinger
 * lægges bagest, så eksisterende skærmbilleder ikke skifter orden.
 */
export const DEPARTMENTS = [
  'gymnastik',
  'svomning',
  'badminton',
  'minigolf',
  'aquapunkt',
  'aquaspinning',
  'skalsibevaegelse',
] as const;
export type Department = (typeof DEPARTMENTS)[number];

/**
 * Navnene, som medlemmerne ser dem.
 *
 * `minigolf` hedder «SGF Minigolf» i Conventus. Inde i appen står der bare
 * «Minigolf», fordi hele appen ér Skals GF — forkortelsen ville kun være
 * støj. Nøglen er den samme; det er kun etiketten, der er kortet af.
 */
export const DEPARTMENT_LABELS: Record<Department, string> = {
  gymnastik: 'Gymnastik',
  svomning: 'Svømning',
  badminton: 'Badminton',
  minigolf: 'Minigolf',
  aquapunkt: 'AquaPunkt',
  aquaspinning: 'AquaSpinning',
  skalsibevaegelse: 'Skals i Bevægelse',
};

/** Etiketten for en afdeling — også når værdien kommer udefra. */
export function departmentLabel(value: string): string {
  return isDepartment(value) ? DEPARTMENT_LABELS[value] : value;
}

export function isDepartment(value: unknown): value is Department {
  return typeof value === 'string' && (DEPARTMENTS as readonly string[]).includes(value);
}

/* ------------------------------------------------------------------ */
/* Holdets art                                                         */
/* ------------------------------------------------------------------ */

/**
 * Alt i appen hed «hold», indtil minigolf kom til.
 *
 * Minigolf ligger i Conventus som to medlemskaber — «Enkeltmedlem» og
 * «Husstandsmedlemsskab» — og ikke som træningshold. Der er ingen ugedag og
 * intet klokkeslæt at hente, fordi der ikke ér nogen: man køber et
 * medlemskab for sæsonen og spiller, når banen er åben.
 *
 * Det er derfor arten står her og ikke bare som et flag. Hele kalenderen
 * bygges ud fra holdets ugedag og første dato. Skulle et medlemskab presses
 * ind i den form, måtte felterne opfindes — og så ville appen fortælle 23
 * minigolfmedlemmer, at de har træning mandag kl. 17. Et forkert svar, der
 * ikke kan rette sig selv, er værre end intet svar.
 *
 * `training`   har træningstider, en kalender, afbud og fremmøde.
 * `membership` har medlemmer og beskeder — og ingen af delene.
 */
export const TEAM_KINDS = ['training', 'membership'] as const;
export type TeamKind = (typeof TEAM_KINDS)[number];

export const TEAM_KIND_LABELS: Record<TeamKind, string> = {
  training: 'Hold',
  membership: 'Medlemskab',
};

export function isTeamKind(value: unknown): value is TeamKind {
  return typeof value === 'string' && (TEAM_KINDS as readonly string[]).includes(value);
}

/* ------------------------------------------------------------------ */
/* Roller                                                              */
/* ------------------------------------------------------------------ */

/**
 * Der er ÉN primær approlle pr. konto (masterprompt §5).
 *
 * Rækkefølgen er stigende adgang og bruges som rangorden i
 * `access/policy.ts`. Ændres den, ændres hvem der må tildele hvad.
 */
export const ROLES = ['member', 'trainer', 'committee', 'board', 'admin'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  member: 'Medlem',
  trainer: 'Instruktør',
  committee: 'Udvalgsmedlem',
  board: 'Bestyrelse',
  admin: 'Administrator',
};

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  member: 'Ingen adgang til administrationen. Ser egne og familiens hold.',
  trainer: 'Adgang til de hold, der er eksplicit tildelt. Flere hold er mulige, også på tværs af afdelinger.',
  committee: 'Adgang til alle hold i den ene manuelt tildelte afdeling.',
  board: 'Alt det, foreningen drives med: alle hold og afdelinger, beskeder, sæson, frivillige og statistik. Kan tildele instruktør og medlem.',
  admin: 'Som bestyrelse, plus Conventus-integrationen og tildeling af bestyrelses-, udvalgs- og administratorrollen.',
};

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

/**
 * Rollens danske navn, slået op fra en vilkårlig streng.
 *
 * Brugerfladen får rollen som tekst fra API'et og kan derfor ikke indeksere
 * `ROLE_LABELS` direkte. Funktionen findes for at rollenavnene kun står ét
 * sted: da administratorrollen kom til, stod de tre steder, og to af dem
 * blev glemt — brugeren så «admin» i stedet for «Administrator».
 */
export function roleLabel(role: string): string {
  return isRole(role) ? ROLE_LABELS[role] : role;
}

/**
 * Rangorden. Et højere tal er mere adgang.
 *
 * Bruges ét sted: til at afgøre, hvilke roller en bruger må TILDELE. Den må
 * aldrig bruges til at afgøre, hvad en bruger må SE eller GØRE — de
 * beslutninger træffes eksplicit pr. område, så en ny rolle ikke kommer til
 * at arve adgang, ingen har taget stilling til.
 */
export const ROLE_RANK: Record<Role, number> = {
  member: 0,
  trainer: 1,
  committee: 2,
  board: 3,
  admin: 4,
};

/** Roller med adgang til administrationen. */
export const STAFF_ROLES: readonly Role[] = ['trainer', 'committee', 'board', 'admin'];

export function isStaffRole(role: Role): boolean {
  return STAFF_ROLES.includes(role);
}

/* ------------------------------------------------------------------ */
/* Holdroller (fra Conventus)                                          */
/* ------------------------------------------------------------------ */

/**
 * Normaliseret holdrolle for en synkroniseret Conventus-person.
 * `unresolved` betyder, at kildens rollemetadata ikke kunne fortolkes sikkert.
 *
 * VIGTIGT: `trainer` her er en HOLDRELATION fra Conventus — ikke appens
 * instruktørrolle. Approllen tildeles kun manuelt (masterprompt §5).
 */
export const TEAM_ROLES = ['participant', 'trainer', 'helper', 'unresolved'] as const;
export type TeamRole = (typeof TEAM_ROLES)[number];

export const TEAM_ROLE_LABELS: Record<TeamRole, string> = {
  participant: 'Deltager',
  trainer: 'Instruktør',
  helper: 'Hjælper',
  unresolved: 'Uafklaret',
};

export function isTeamRole(value: unknown): value is TeamRole {
  return typeof value === 'string' && (TEAM_ROLES as readonly string[]).includes(value);
}

/* ------------------------------------------------------------------ */
/* Beskeder                                                            */
/* ------------------------------------------------------------------ */

export const AUDIENCES = ['all', 'department', 'team', 'user', 'team_trainers'] as const;
export type Audience = (typeof AUDIENCES)[number];

export const AUDIENCE_LABELS: Record<Audience, string> = {
  all: 'Hele foreningen',
  department: 'Afdeling',
  team: 'Hold',
  user: 'Privat besked',
  team_trainers: 'Holdets instruktører',
};

export function isAudience(value: unknown): value is Audience {
  return typeof value === 'string' && (AUDIENCES as readonly string[]).includes(value);
}

export const PRIORITIES = ['normal', 'important'] as const;
export type Priority = (typeof PRIORITIES)[number];

export const PRIORITY_LABELS: Record<Priority, string> = {
  normal: 'Normal',
  important: 'Vigtig',
};

export const ANNOUNCEMENT_TITLE_MAX = 100;
export const ANNOUNCEMENT_BODY_MAX = 800;

/** Indbakken viser de seneste op til 20 relevante beskeder (masterprompt §10). */
export const INBOX_LIMIT = 20;

/* ------------------------------------------------------------------ */
/* Svar til afsenderen                                                 */
/* ------------------------------------------------------------------ */

/**
 * Et svar er et spørgsmål, ikke et essay: «hvad tid slutter I på lørdag?».
 * Grænsen er sat lavere end en besked med vilje — svaret skal kunne læses
 * på en telefon uden at rulle.
 */
export const REPLY_BODY_MAX = 500;
/** Højst ti svar pr. konto pr. kvarter, så en afsender ikke kan oversvømmes. */
export const REPLY_RATE_LIMIT = 10;
export const REPLY_RATE_WINDOW_MS = 15 * 60 * 1000;

/**
 * Hvornår svar er slået til, når afsenderen ikke vælger noget.
 *
 * En privat besked og en besked til instruktørerne ER en samtale — der giver
 * svar sig selv. En udmelding til hele foreningen er det ikke: dér skal
 * afsenderen aktivt bede om svar, ellers får han 300 «tak!» i indbakken.
 */
export function repliesEnabledByDefault(audience: Audience): boolean {
  return audience === 'user' || audience === 'team_trainers' || audience === 'team';
}

/* ------------------------------------------------------------------ */
/* Afbud                                                               */
/* ------------------------------------------------------------------ */

export const ABSENCE_REASONS = ['sygdom', 'ferie', 'andet'] as const;
export type AbsenceReason = (typeof ABSENCE_REASONS)[number];

export const ABSENCE_REASON_LABELS: Record<AbsenceReason, string> = {
  sygdom: 'Sygdom',
  ferie: 'Ferie',
  andet: 'Andet',
};

export function isAbsenceReason(value: unknown): value is AbsenceReason {
  return typeof value === 'string' && (ABSENCE_REASONS as readonly string[]).includes(value);
}

/* ------------------------------------------------------------------ */
/* Fremmøde                                                            */
/* ------------------------------------------------------------------ */

export const ATTENDANCE_STATUSES = ['present', 'absent'] as const;
export type AttendanceStatus = (typeof ATTENDANCE_STATUSES)[number];

export const ATTENDANCE_STATUS_LABELS: Record<AttendanceStatus, string> = {
  present: 'Til stede',
  absent: 'Fraværende',
};

/** «Ikke registreret» er fraværet af en record — ikke en gemt status. */
export const ATTENDANCE_NOTE_MAX = 160;

export function isAttendanceStatus(value: unknown): value is AttendanceStatus {
  return typeof value === 'string' && (ATTENDANCE_STATUSES as readonly string[]).includes(value);
}

/* ------------------------------------------------------------------ */
/* Notifikationspræferencer                                            */
/* ------------------------------------------------------------------ */

export const NOTIFICATION_PREFERENCE_KEYS = [
  'cancellations',
  'urgentMessages',
  'teamMessages',
  'departmentNews',
  'calendarReminders',
  'absenceConfirmations',
] as const;
export type NotificationPreferenceKey = (typeof NOTIFICATION_PREFERENCE_KEYS)[number];

export type NotificationPreferences = Record<NotificationPreferenceKey, boolean>;

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  cancellations: true,
  urgentMessages: true,
  teamMessages: true,
  departmentNews: true,
  calendarReminders: true,
  absenceConfirmations: true,
};

export const NOTIFICATION_PREFERENCE_LABELS: Record<NotificationPreferenceKey, string> = {
  cancellations: 'Aflysninger og flytninger',
  urgentMessages: 'Vigtige beskeder',
  teamMessages: 'Holdbeskeder',
  departmentNews: 'Afdelingsnyt',
  calendarReminders: 'Kalenderpåmindelser',
  absenceConfirmations: 'Afbudsbekræftelser',
};

/**
 * ALLE seks indstillinger udløser nu en rigtig udsendelse.
 *
 * Tidligere blev `calendarReminders` og `absenceConfirmations` gemt uden at
 * gøre noget. En indstilling, der ikke virker, er værre end ingen
 * indstilling: den lover brugeren noget, appen ikke holder. Derfor er de
 * bygget færdige — se `server/repo/notifications.ts`.
 */
export const UNIMPLEMENTED_NOTIFICATION_KEYS: readonly NotificationPreferenceKey[] = [];

export const NOTIFICATION_PREFERENCE_HINTS: Record<NotificationPreferenceKey, string> = {
  cancellations: 'Når en træning aflyses eller flyttes.',
  urgentMessages: 'Beskeder, afsenderen har markeret som vigtige.',
  teamMessages: 'Beskeder til dit hold og private beskeder.',
  departmentNews: 'Nyt fra foreningen og fra din afdeling.',
  calendarReminders: 'En påmindelse aftenen før træning.',
  absenceConfirmations: 'Kvittering, når du har meldt afbud.',
};

/** Påmindelsen sendes aftenen før, fra kl. 17 dansk tid. */
export const REMINDER_HOUR_LOCAL = 17;
/** Der påmindes om træninger inden for de næste 24 timer. */
export const REMINDER_WINDOW_HOURS = 24;

/* ------------------------------------------------------------------ */
/* Frivillige                                                          */
/* ------------------------------------------------------------------ */

export const VOLUNTEER_TITLE_MIN = 3;
export const VOLUNTEER_TITLE_MAX = 100;
export const VOLUNTEER_LOCATION_MIN = 2;
export const VOLUNTEER_LOCATION_MAX = 100;
export const VOLUNTEER_CAPACITY_MIN = 1;
export const VOLUNTEER_CAPACITY_MAX = 100;

/* ------------------------------------------------------------------ */
/* Familieforbindelse                                                  */
/* ------------------------------------------------------------------ */

/** Challenge er gyldig i 10 minutter (masterprompt §6). */
export const CHALLENGE_TTL_MS = 10 * 60 * 1000;
/** Højst fem challenge-starter pr. konto pr. 10 minutter. */
export const CHALLENGE_RATE_LIMIT = 5;
export const CHALLENGE_RATE_WINDOW_MS = 10 * 60 * 1000;
/** Tillad 1–20 unikke profiler og højst én «mig selv». */
export const FAMILY_LINK_MIN = 1;
export const FAMILY_LINK_MAX = 20;

export type FamilyRelation = 'self' | 'child';

/* ------------------------------------------------------------------ */
/* Kalender                                                            */
/* ------------------------------------------------------------------ */


export const WEEKDAYS = [
  'mandag',
  'tirsdag',
  'onsdag',
  'torsdag',
  'fredag',
  'lørdag',
  'søndag',
] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  mandag: 'Mandag',
  tirsdag: 'Tirsdag',
  onsdag: 'Onsdag',
  torsdag: 'Torsdag',
  fredag: 'Fredag',
  lørdag: 'Lørdag',
  søndag: 'Søndag',
};

/** ISO-8601: mandag = 1 … søndag = 7. */
export const WEEKDAY_ISO: Record<Weekday, number> = {
  mandag: 1,
  tirsdag: 2,
  onsdag: 3,
  torsdag: 4,
  fredag: 5,
  lørdag: 6,
  søndag: 7,
};

export function isWeekday(value: unknown): value is Weekday {
  return typeof value === 'string' && (WEEKDAYS as readonly string[]).includes(value);
}

/* ------------------------------------------------------------------ */
/* Statistik                                                           */
/* ------------------------------------------------------------------ */

/** Samme konto + sti tælles kun én gang pr. 30-minutters interval (§13). */
export const ACTIVITY_BUCKET_MS = 30 * 60 * 1000;
/** Hensigt om 400 dages opbevaring. Oprydningen er implementeret og testet. */
export const ACTIVITY_RETENTION_DAYS = 400;

/* ------------------------------------------------------------------ */
/* Identitet                                                           */
/* ------------------------------------------------------------------ */

export const TIMEZONE = 'Europe/Copenhagen';
export const LOCALE = 'da-DK';

/** Foreningens identitet (masterprompt §4). */
export const BRAND = {
  name: 'Skals GF',
  fullName: 'Skals Gymnastikforening',
  tagline: 'Fællesskab i bevægelse',
} as const;
